import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Play,
  Clock,
  Calendar,
  User,
  GraduationCap,
  Video,
  FileText,
  Lightbulb,
  Target,
  ExternalLink,
  BookOpen,
  Share2,
  Heart,
  Bookmark,
} from "lucide-react";
import { AffiliateHeaderContext } from "../../context/AffiliateHeaderContext";
import {
  getPublishedEducationContent,
  getYouTubeVideoId,
  getYouTubeEmbedUrl,
} from "../../lib/educationService";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Alert from "../ui/Alert";

const EducationDetailPage = () => {
  const { affiliateName, educationId } = useParams();
  const navigate = useNavigate();
  const decodedAffiliateName = decodeURIComponent(affiliateName);
  const setHeaderContent = useContext(AffiliateHeaderContext);

  const [educationContent, setEducationContent] = useState(null);
  const [allContent, setAllContent] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set header content
  useEffect(() => {
    if (educationContent) {
      setHeaderContent(
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            onClick={() => navigate(`/affiliate/${affiliateName}/education`)}
            className="bg-white/10 hover:bg-white/20 text-white border-white/20 -ml-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Education
          </Button>
          <div className="w-2 h-8 bg-gradient-to-b from-[#B8460E] to-[#8B3A0E] rounded-full"></div>
          <div>
            <h1 className="text-2xl font-bold text-white line-clamp-1">
              {educationContent.title}
            </h1>
            <p className="text-gray-400 text-sm">
              {educationContent.videoUrl ? "Video Course" : "Written Guide"}
            </p>
          </div>
        </div>
      );
    }
    return () => setHeaderContent(null);
  }, [setHeaderContent, educationContent, navigate, affiliateName]);

  // Load education content
  useEffect(() => {
    const loadEducationContent = async () => {
      try {
        setIsLoading(true);
        setError(null);

        console.log("🚀 Loading education content for ID:", educationId);

        const content = await getPublishedEducationContent();
        setAllContent(content);

        // Find the specific content item
        const selectedContent = content.find((item) => item.id === educationId);

        if (!selectedContent) {
          setError("Education content not found.");
          return;
        }

        console.log("✅ Found education content:", selectedContent.title);
        setEducationContent(selectedContent);
      } catch (err) {
        console.error("❌ Error loading education content:", err);
        setError("Failed to load education content. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    if (educationId) {
      loadEducationContent();
    }
  }, [educationId]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const renderYouTubeEmbed = (videoUrl) => {
    const videoId = getYouTubeVideoId(videoUrl);
    if (!videoId) return null;

    const embedUrl = getYouTubeEmbedUrl(videoId);

    return (
      <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-black/20 backdrop-blur-xl border border-white/10 shadow-2xl mb-8">
        <iframe
          src={embedUrl}
          title="Educational Video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        ></iframe>
      </div>
    );
  };

  const renderContent = (content) => {
    if (!content) return null;

    return (
      <div
        className="prose prose-invert prose-xl max-w-none leading-relaxed"
        dangerouslySetInnerHTML={{ __html: content }}
        style={{
          "--tw-prose-body": "rgb(229 231 235)",
          "--tw-prose-headings": "rgb(255 255 255)",
          "--tw-prose-links": "rgb(184 70 14)",
          "--tw-prose-bold": "rgb(255 255 255)",
          "--tw-prose-counters": "rgb(156 163 175)",
          "--tw-prose-bullets": "rgb(156 163 175)",
          "--tw-prose-quotes": "rgb(229 231 235)",
          "--tw-prose-quote-borders": "rgb(75 85 99)",
          "--tw-prose-code": "rgb(229 231 235)",
          "--tw-prose-pre-code": "rgb(229 231 235)",
          "--tw-prose-pre-bg": "rgb(31 41 55)",
          "--tw-prose-th-borders": "rgb(75 85 99)",
          "--tw-prose-td-borders": "rgb(55 65 81)",
          lineHeight: "1.8",
        }}
      />
    );
  };

  const getRelatedContent = () => {
    if (!allContent || !educationContent) return [];

    return allContent
      .filter((item) => item.id !== educationContent.id)
      .slice(0, 3);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#B8460E] mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading education content...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <Alert type="error">
          <p className="font-semibold">Error Loading Education Content</p>
          <p>{error}</p>
          <Button
            onClick={() => navigate(`/affiliate/${affiliateName}/education`)}
          >
            Back to Education
          </Button>
        </Alert>
      </div>
    );
  }

  if (!educationContent) {
    return (
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-12 text-center">
        <div className="w-16 h-16 bg-[#B8460E]/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <GraduationCap className="w-8 h-8 text-[#B8460E]" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">
          Content Not Found
        </h3>
        <p className="text-gray-400 mb-6">
          The requested education content could not be found.
        </p>
        <Button
          onClick={() => navigate(`/affiliate/${affiliateName}/education`)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Education
        </Button>
      </div>
    );
  }

  const relatedContent = getRelatedContent();

  return (
    <div className="space-y-8">
      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Main Article */}
        <div className="lg:col-span-3">
          <Card className="relative overflow-hidden">
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-0 left-0 w-96 h-96 bg-[#B8460E]/5 rounded-full blur-3xl animate-pulse"></div>
              <div
                className="absolute bottom-0 right-0 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl animate-pulse"
                style={{ animationDelay: "2s" }}
              ></div>
            </div>

            <div className="relative z-10 p-8">
              {/* Content Header */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  {educationContent.videoUrl ? (
                    <div className="flex items-center gap-2 px-3 py-1 bg-[#B8460E]/20 text-[#B8460E] rounded-full text-sm font-medium">
                      <Video className="h-4 w-4" />
                      <span>Video Course</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm font-medium">
                      <FileText className="h-4 w-4" />
                      <span>Written Guide</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1 text-gray-400 text-sm">
                    <Calendar className="h-4 w-4" />
                    <span>{formatDate(educationContent.createdAt)}</span>
                  </div>
                </div>

                <h1 className="text-4xl font-bold text-white mb-4 leading-tight">
                  {educationContent.title}
                </h1>

                {educationContent.description && (
                  <p className="text-xl text-gray-300 leading-relaxed">
                    {educationContent.description}
                  </p>
                )}
              </div>

              {/* Video embed if available */}
              {educationContent.videoUrl &&
                renderYouTubeEmbed(educationContent.videoUrl)}

              {/* Content body */}
              {educationContent.content && (
                <div className="prose-container mb-8">
                  {renderContent(educationContent.content)}
                </div>
              )}

              {/* Action buttons */}
              <div className="flex flex-wrap items-center gap-4 pt-8 border-t border-white/10">
                {educationContent.videoUrl && (
                  <Button
                    onClick={() =>
                      window.open(educationContent.videoUrl, "_blank")
                    }
                    className="bg-[#B8460E] hover:bg-[#8B3A0E] text-white"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Watch on YouTube
                  </Button>
                )}

                <div className="flex items-center gap-6 text-sm text-gray-400">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="h-4 w-4" />
                    <span>
                      Apply these strategies to maximize your affiliate success
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          {/* Quick Stats */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-white mb-4">
              Learning Progress
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Content Type</span>
                <span className="text-white font-medium">
                  {educationContent.videoUrl ? "Video" : "Article"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Updated</span>
                <span className="text-white font-medium">
                  {formatDate(educationContent.updatedAt)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Order</span>
                <span className="text-white font-medium">
                  #{educationContent.orderIndex + 1}
                </span>
              </div>
            </div>
          </Card>

          {/* Related Content */}
          {relatedContent.length > 0 && (
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-[#B8460E]" />
                Related Content
              </h3>
              <div className="space-y-4">
                {relatedContent.map((content) => (
                  <button
                    key={content.id}
                    onClick={() =>
                      navigate(
                        `/affiliate/${affiliateName}/education/${content.id}`
                      )
                    }
                    className="w-full text-left p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-all duration-200 group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-[#B8460E] rounded-full mt-2 flex-shrink-0"></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm line-clamp-2 group-hover:text-[#B8460E] transition-colors">
                          {content.title}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          {content.videoUrl ? (
                            <Video className="h-3 w-3 text-[#B8460E]" />
                          ) : (
                            <FileText className="h-3 w-3 text-blue-400" />
                          )}
                          <span className="text-xs text-gray-500">
                            {content.videoUrl ? "Video" : "Article"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </Card>
          )}

          {/* Call to Action */}
          <Card className="p-6 bg-gradient-to-br from-[#B8460E]/10 to-[#8B3A0E]/10 border-[#B8460E]/20">
            <div className="text-center">
              <Target className="h-8 w-8 text-[#B8460E] mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">
                Ready to Apply?
              </h3>
              <p className="text-gray-300 text-sm mb-4">
                Put these strategies into practice and track your performance.
              </p>
              <Button
                onClick={() => navigate(`/affiliate/${affiliateName}`)}
                className="w-full bg-[#B8460E] hover:bg-[#8B3A0E] text-white"
              >
                View Performance
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EducationDetailPage;
